import BlueSmiley0 from '../assets/BlueSmiley0.svg';
import BlueSmiley40 from '../assets/BlueSmiley40.svg';
import BlueSmiley60 from '../assets/BlueSmiley60.svg';
import BlueSmiley80 from '../assets/BlueSmiley80.svg';
import BlueSmiley100 from '../assets/BlueSmiley100.svg';
import PurpleSmiley0 from '../assets/PurpleSmiley0.svg';
import PurpleSmiley40 from '../assets/PurpleSmiley40.svg';
import PurpleSmiley60 from '../assets/PurpleSmiley60.svg';
import PurpleSmiley80 from '../assets/PurpleSmiley80.svg';
import PurpleSmiley100 from '../assets/PurpleSmiley100.svg';
import { Person } from '../types';
import { useMonthlyInteraction } from './use-interaction';
import { useOneOnOnesAccess } from './use-one-on-ones-access';

interface UseProgressImageProps {
	progress: number;
	type: 'feedback' | 'recognition';
}

export const useProgressImage = ({ progress, type }: UseProgressImageProps) => {
	const svgPos = {
		position: 'absolute',
		top: 0,
		left: `calc(${progress}% - ${progress * 0.01 * 24}px)`,
	} as React.CSSProperties;

	let face;

	switch (true) {
		case progress < 40:
			face = type === 'recognition' ? BlueSmiley0 : PurpleSmiley0;
			break;
		case progress < 60:
			face = type === 'recognition' ? BlueSmiley40 : PurpleSmiley40;
			break;
		case progress < 80:
			face = type === 'recognition' ? BlueSmiley60 : PurpleSmiley60;
			break;
		case progress < 100:
			face = type === 'recognition' ? BlueSmiley80 : PurpleSmiley80;
			break;
		default:
			face = type === 'recognition' ? BlueSmiley100 : PurpleSmiley100;
			break;
	}

	return {
		face,
		svgPos,
	};
};

export const useFeedbackProgress = (people: Person[]) => {
	const { canAccessOneOnOnes } = useOneOnOnesAccess();

	const directReportsTotal = people.length;

	const { interactionPeopleInMonth: feedbackPeopleInMonth } = useMonthlyInteraction({ people, type: 'feedback' });
	const directReportsGivenFeedback = feedbackPeopleInMonth.length;

	const feedbackProgress = directReportsTotal < 1 ? 0 : (directReportsGivenFeedback / directReportsTotal) * 100;

	// We have to correct for the width of the SVG
	const feedbackVisualProgress = feedbackProgress + (100 - feedbackProgress) * 0.01 * 9;

	const { face: feedbackFace, svgPos: feedbackSvgPos } = useProgressImage({
		progress: feedbackProgress,
		type: 'feedback',
	});

	if (!canAccessOneOnOnes) {
		return {
			feedbackProgress: 0,
			feedbackVisualProgress: 0,
		};
	}

	return {
		directReportsTotal,
		directReportsGivenFeedback,
		feedbackFace,
		feedbackSvgPos,
		feedbackProgress,
		feedbackVisualProgress,
	};
};

export const useRecognitionProgress = (people: Person[]) => {
	const directReportsTotal = people.length;

	// Recognition
	const { interactionPeopleInMonth: recognizedPeopleInMonth } = useMonthlyInteraction({ people, type: 'recognition' });
	const directReportsRecognized = recognizedPeopleInMonth.length;

	const recognitionProgress = directReportsTotal < 1 ? 0 : (directReportsRecognized / directReportsTotal) * 100;

	// We have to correct for the width of the SVG
	const recognitionVisualProgress = recognitionProgress + (100 - recognitionProgress) * 0.01 * 9;

	const { face: recognitionFace, svgPos: recognitionSvgPos } = useProgressImage({
		progress: recognitionProgress,
		type: 'recognition',
	});

	return {
		directReportsRecognized,
		directReportsTotal,
		recognitionFace,
		recognitionSvgPos,
		recognitionProgress,
		recognitionVisualProgress,
	};
};
